/**
 * The customer's answer to the cookie banner, in a cookie shared with the marketing site
 * (capacityvault-marketing, src/lib/analytics.js). It is set on the parent domain, so a visitor
 * who answered on www.capacityvault.co.uk is not asked again here. Remembering the choice is
 * strictly necessary, so it may be stored whatever the answer.
 *
 * Kept apart from analyticsService.ts so the banner and the footer can read the choice without
 * pulling in the container. Mirrors src/analytics/consentState.ts and consent.ts in lpa.ui.
 */
export type ConsentChoice = 'granted' | 'denied';

export const CONSENT_COOKIE = 'cv_analytics_consent';
const COOKIE_DOMAIN = 'capacityvault.co.uk';
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;
const GOOGLE_COOKIE = /^(_ga|_gid|_gat|_gcl)/;

// The choice made during THIS visit. It wins over the cookie, so an answer still holds for the
// rest of the visit in a browser that blocks cookies (it will simply be asked again next time).
let sessionChoice: ConsentChoice | undefined;

// The customer's choice, or undefined when they have not been asked yet.
export const readConsent = (): ConsentChoice | undefined => {
  if (sessionChoice) {
    return sessionChoice;
  }

  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=(granted|denied)(?:;|$)`));
  return match ? (match[1] as ConsentChoice) : undefined;
};

// Remembers the choice: for this visit always, across visits and sites when cookies allow.
export const storeConsent = (choice: ConsentChoice): void => {
  sessionChoice = choice;
  const { hostname, protocol } = window.location;
  let cookie = `${CONSENT_COOKIE}=${choice}; max-age=${ONE_YEAR_SECONDS}; path=/; SameSite=Lax`;
  if (hostname === COOKIE_DOMAIN || hostname.endsWith(`.${COOKIE_DOMAIN}`)) {
    cookie += `; domain=${COOKIE_DOMAIN}`;
  }
  if (protocol === 'https:') {
    cookie += '; Secure';
  }
  document.cookie = cookie;
};

// Test seam: forget the in-memory choice (the cookie is cleared by the caller).
export const forgetSessionConsent = (): void => {
  sessionChoice = undefined;
};

// Google's documented kill switch: window['ga-disable-<measurement id>'] stops every hit for that
// id at once. The ids are read from the loaded container, so the app never needs to know them.
export const setGoogleAnalyticsDisabled = (disabled: boolean): void => {
  const loaded = (window as unknown as { google_tag_manager?: Record<string, unknown> }).google_tag_manager;
  for (const id of Object.keys(loaded ?? {})) {
    if (/^G-[A-Z0-9]+$/i.test(id)) {
      (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = disabled;
    }
  }
};

// Expires Google's analytics cookies on this host and every parent domain they could be set on.
export const deleteGoogleCookies = (): void => {
  const names = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => GOOGLE_COOKIE.test(name));
  if (names.length === 0) {
    return;
  }

  const labels = window.location.hostname.split('.');
  const domains = ['', ...labels.slice(0, -1).map((_, i) => `; domain=.${labels.slice(i).join('.')}`)];
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }
  }
};
